import { GridColDef, GridRenderCellParams, GridSortModel, GridValueGetterParams } from "@mui/x-data-grid-pro";
import { useEffect } from "react";
import { Observable } from "rxjs/internal/Observable";
import { Subscription } from "rxjs/internal/Subscription";
import zustand from "zustand";
import { createAxios, isError401, isErrorCanceled, useCommStore } from "./axios";
import { EnumItemDto } from "./enum";
import shallow from "zustand/shallow";
import { UserTypeId, useAccountStore } from "./account";
import { BehaviorSubject, Subject, tap } from "rxjs";
import { orderBy } from "lodash";
import { delay } from "./utils";

export const enumAllValue = 0;

export const defaultItemsPerPage = 10;

export interface ListDto<T> {
    count?: number;
    items: T[];
}

export interface FilterDto {
    items: FilterItemDto[];
}

export interface FilterItemDto {
    properties: string[];
    value: any;
    operator: keyof typeof FilterItemOperator;
}

export enum FilterItemOperator {
    Like,
    Equal,
    NotEqual,
    GreaterThanOrEqual,
    GreaterThan,
    LessThanOrEqual,
    LessThan,
    In,
    Between,
}

export interface PageDto {
    index: number;
    size: number;
}

export enum SortDirection {
    Ascending,
    Descending
}

export interface SortDto {
    property: string;
    direction: keyof typeof SortDirection;
}

export interface FilterSortPageDto {
    filter?: FilterDto;
    sort?: SortDto;
    page?: PageDto;
    count?: boolean;
}

export type DataFilterValue = string | number | boolean | null | (string | number | boolean | null)[];

export interface DataFilter<T> {
    props: (keyof T)[];
    value: DataFilterValue;
    valueFormatted?: string | null | (string | null | undefined)[];
    operator: keyof typeof FilterItemOperator;
    fullText?: boolean;
}

export interface DataFilters<T> {
    [filter: string]: DataFilter<T>;
};

export interface ListStore<T> {
    config: ListStoreConfig<T>;
    path: string | undefined;
    qs?: { [name: string]: string | number | null | undefined; };
    items: T[];
    filteredSortedItems: T[];
    selectedItem: T | null;
    count: number;
    fetching: boolean;
    fetched: boolean;
    fetchFailed: boolean;
    abortController: AbortController | null;
    initialized: boolean;
    dataFilters: DataFilters<T>;
    fetchDataFilters: DataFilters<T>;
    externalDataFilters: DataFilter<T>[];
    externalFetchDataFilters: DataFilter<T>[];
    itemsPerPage?: number;
    sortModel: GridSortModel;
    dataSubscription: Subscription | null,
    initializedCount: number,
    queryFilter: FilterDto | null,
    items$: Observable<{ items: T[]; isFetching: boolean; }>;
    selectedItem$: BehaviorSubject<T | null>;
    listProps: ListProp<T>[];
    listPropsId: number;
    onFetch: Observable<{ unintrusive?: boolean | null }>;
    selectedItemId: string | number | undefined;
    isUnintrusiveFetch: boolean;
    fetch(params?: { updateFetchFilterData?: true, forceFetch?: false; unintrusive?: true; }): void;
    init(): void;
    uninit(): void;
    dispose(): void;
    setFilterData(name: string, data: DataFilter<T> | null): void;
    resetFilterData(): void;
    setSortModel(model: GridSortModel): void;
    setSelectedItem(selectedItem: T | null): void;
    setPath(path: string | undefined): void;
    setQs(qs: { [name: string]: string | number | null | undefined; }): void;
    setItems(items: T[], isFetching: boolean): void;
    setListProps(listProps: ListProp<T>[]): void;
    setExternalDataFilters(filters: DataFilter<T>[]): void;
}

export interface ListProp<T> {
    label: string;
    column?: keyof T;
    columnKey?: string;
    width?: number;
    filter?: (keyof T)[] | true;
    filterWidth?: number;
    filterWidthFlexGrow?: number;
    operator?: keyof typeof FilterItemOperator;
    filterFullText?: boolean;
    sortable?: boolean;
    enumStore?: UseListStore<EnumItemDto>;
    enum?: EnumItemDto[];
    type?: "currency" | "date" | "datetime" | "bool" | "number";
    align?: "right";
    flex?: GridColDef["flex"];
    readOnlyValue?: (params: { userTypeId: UserTypeId | null | undefined; }) => string | number | (string | number)[] | null;
    transformFilterValue?: (value: DataFilterValue) => DataFilterValue;
    autoFocus?: boolean;
    renderCell?: (params: GridRenderCellParams) => React.ReactNode;
    valueGetter?: (params: GridValueGetterParams) => any;
}

export interface ListStoreConfig<T> {
    listProps?: (ListProp<T> | false)[];
    autoInitialFetch?: boolean;
    addIndexId?: boolean;
    getId?(item: T): string | number;
    supportsApiFilterSortPage?: true;
    path?: string;
    data?: Observable<{ items: T[]; isFetching: boolean; }>;
    itemsPerPage?: number;
    itemsPerPageMin?: number;
    itemsPerPageMax?: number;
    autoSelectFirstItem?: true;
    selectedItemId?: string | number;
    qs?: { [name: string]: string | number | null | undefined; };
    onFetched?(): void;
    getFetchBody?: () => any;
}

function addIdIndex<T>(items: T[]) {
    return items.map((p, i) => ({ id: i, ...p }));
}

export function getListPropFilterProps<T>(prop: ListProp<T>) {
    return prop.filter == null ? [] : prop.filter === true ? [prop.column!] : prop.filter;
}

export function getListPropFilterName<T>(item: ListProp<T>): string {
    return item.filter === true ? item.column as string : item.filter!.join(";");
}

function applyFilters<T>(items: T[], store: ListStore<T>) {
    for (const filter of Object.values(store.fetchDataFilters).concat(store.externalFetchDataFilters)) {
        if (filter.operator === "In") {
            const value = filter.value as (string | number | boolean)[];
            items = items.filter(p => filter.props.some(r => value.indexOf(p[r] as string | number | boolean) >= 0));
        } else if (filter.operator === "Like") {
            const value = filter.value as string;
            const regex = new RegExp(escapeRegExp(value).replace(/%/g, ".*").normalize("NFD").replace(/[\u0300-\u036f]/g, ""), "i");
            items = items.filter(p => filter.props.some(r => regex.test(((p[r] ?? "") as string).normalize("NFD").replace(/[\u0300-\u036f]/g, ""))));
        } else if (filter.operator === "Equal") {
            const value = filter.value;
            items = items.filter(p => filter.props.some(r => p[r] === value));
        } else if (filter.operator === "NotEqual") {
            const value = filter.value;
            items = items.filter(p => filter.props.some(r => p[r] !== value));
        } else if (filter.operator === "Between") {
            const value = filter.value as (string | number | null)[] || [];
            const from = value[0];
            const to = value[1];
            items = items.filter(p => filter.props.some(r => (from == null || p[r] >= from) && (to == null || p[r] <= to)));
        } else if (filter.operator === "GreaterThan") {
            const value = filter.value as string | number;
            items = items.filter(p => filter.props.some(r => p[r] > value));
        } else if (filter.operator === "LessThan") {
            const value = filter.value as string | number;
            items = items.filter(p => filter.props.some(r => p[r] < value));
        } else if (filter.operator === "GreaterThanOrEqual") {
            const value = filter.value as string | number;
            items = items.filter(p => filter.props.some(r => p[r] >= value));
        } else if (filter.operator === "LessThanOrEqual") {
            const value = filter.value as string | number;
            items = items.filter(p => filter.props.some(r => p[r] <= value));
        }
    }
    return items;
}

function escapeRegExp(text: string) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function applySort<T>(items: T[], model: GridSortModel) {
    items = orderBy(items, model.map(p => p.field), model.map(p => p.sort || "asc"));
    return items;
}

export function getFilterDto<T>(store: ListStore<T>): FilterDto {
    function getFinalFilterValue(filter: DataFilter<T>) {
        return filter == null ? null : filter.operator === "Like" ? (filter.fullText ? "%" : "") + filter.value + "%" : filter.value;
    }
    let filters = store.fetchDataFilters;
    const accountStore = useAccountStore.getState().data;
    for (const p of store.listProps || []) {
        if (p.readOnlyValue) {
            const v = p.readOnlyValue(accountStore?.accessTokenDecoded ?? { userTypeId: null });
            if (v != null) {
                filters = { ...filters };
                filters[getListPropFilterName(p)] = {
                    props: getListPropFilterProps(p),
                    operator: "Equal",
                    value: v,
                };
            }
        }
        if (p.transformFilterValue) {
            const filterPropName = getListPropFilterName(p);
            const filter = filters[filterPropName];
            if (filter != null) {
                filters = { ...filters };
                filters[filterPropName] = {
                    ...filter,
                    value: p.transformFilterValue(filter.value),
                };
            }
        }
    }
    return {
        items: Object.values(filters).concat(store.externalFetchDataFilters)
            .map(p => ({
                properties: p.props as string[],
                value: getFinalFilterValue(p),
                operator: p.operator,
            }))
    };
}

export function getSort<T>(store: ListStore<T>): SortDto | undefined {
    return store.sortModel.length !== 1 ? undefined : {
        property: store.sortModel[0].field,
        direction: store.sortModel[0].sort === "asc" ? "Ascending" : "Descending",
    }
}

export function createListStore<T>(config: ListStoreConfig<T> = {}): UseListStore<T> {
    const itemsSubject = new BehaviorSubject<{ items: T[]; isFetching: boolean; }>({
        items: [],
        isFetching: false,
    });
    const onFetchSubject = new Subject<{ unintrusive?: boolean | null }>();
    const useStore = zustand<ListStore<T>>((set, get) => ({
        config,
        path: config.path,
        qs: config.qs,
        items: [],
        filteredSortedItems: [],
        selectedItem: null,
        count: 0,
        fetching: false,
        fetched: false,
        fetchFailed: false,
        abortController: null,
        initialized: false,
        dataFilters: {},
        fetchDataFilters: {},
        externalDataFilters: [],
        externalFetchDataFilters: [],
        itemsPerPage: config.itemsPerPage,
        sortModel: [],
        dataSubscription: null,
        initializedCount: 0,
        queryFilter: null,
        items$: itemsSubject.pipe(tap({
            subscribe() {
                get().init();
            },
            unsubscribe() {
                get().dispose();
            },
        })),
        selectedItem$: new BehaviorSubject<T | null>(null),
        listProps: (config.listProps || []).filter(p => p) as ListProp<T>[],
        listPropsId: 1,
        onFetch: onFetchSubject,
        selectedItemId: config.selectedItemId,
        isUnintrusiveFetch: false,
        fetch(params) {
            if (params?.updateFetchFilterData) {
                set({ fetchDataFilters: get().dataFilters, externalFetchDataFilters: get().externalDataFilters });
            }
            if (params?.forceFetch !== false) {
                onFetchSubject.next({ unintrusive: params?.unintrusive });
            }
            if (config.data) {
                const filteredItems = applyFilters(get().items, get());
                const filteredSortedItems = applySort(filteredItems, get().sortModel);
                set({ filteredSortedItems, count: filteredSortedItems.length });
                refreshSelectedItem(get());
                autoSelectFirstItem(get());
                return;
            }
            const currentAbortController = get().abortController;
            if (currentAbortController) {
                currentAbortController.abort();
            }
            const abortController = new AbortController();
            set({ fetching: true, abortController, isUnintrusiveFetch: params?.unintrusive === true });
            itemsSubject.next({ items: get().items, isFetching: get().fetching });
            const path = get().path;
            (async () => {
                try {
                    if (config.supportsApiFilterSortPage) {
                        if (path != null) {
                            const filter = getFilterDto(get());
                            const { data, items } = await listStoreFetch(get(), filter, { index: 0, size: 1000 }, false, abortController.signal, get().qs);
                            set(state => ({
                                items, filteredSortedItems: items,
                                count: data.count == null ? state.count : data.count,
                                queryFilter: filter,
                                initialized: true,
                                fetched: true,
                                fetchFailed: false,
                            }));
                        }
                    } else {
                        if (!get().initialized || params?.forceFetch == null) {
                            if (path != null) {
                                const body = config.getFetchBody?.();
                                while (true) {
                                    useCommStore.getState().requestStarted();
                                    try {
                                        const { data } = body ?
                                            await createAxios().post<T[]>(path, body, { signal: abortController.signal, params: get().qs }) :
                                            await createAxios().get<T[]>(path, { signal: abortController.signal, params: get().qs });
                                        const items = config.addIndexId ? addIdIndex(data) : data;
                                        const filteredItems = applyFilters(items, get());
                                        const filteredSortedItems = applySort(filteredItems, get().sortModel);
                                        set({ items, filteredSortedItems, count: filteredSortedItems.length, initialized: true, fetched: true, fetchFailed: false });
                                        break;
                                    } catch (error) {
                                        if (isErrorCanceled(error) || isError401(error)) {
                                            throw error;
                                        }

                                        await delay(1000);
                                    } finally {
                                        useCommStore.getState().requestFinished(null);
                                    }
                                }
                            } else {
                                set({ items: [], filteredSortedItems: [], count: 0, initialized: true, fetched: true, fetchFailed: false });
                            }
                        } else {
                            const filteredItems = applyFilters(get().items, get());
                            const filteredSortedItems = applySort(filteredItems, get().sortModel);
                            set({ filteredSortedItems, count: filteredSortedItems.length, fetched: true, fetchFailed: false });
                        }
                    }
                } catch {
                    set({ fetchFailed: true });
                } finally {
                    if (abortController.signal.aborted) {
                        return;
                    }
                    set({ fetching: false, abortController: null });
                    itemsSubject.next({ items: get().items, isFetching: get().fetching });
                    refreshSelectedItem(get());
                    autoSelectFirstItem(get());
                    config.onFetched?.();
                }
            })();
        },
        init() {
            set(p => ({ initializedCount: p.initializedCount + 1 }));
            if (get().initialized && get().initializedCount > 1) {
                return;
            }
            if (config.data && get().dataSubscription == null) {
                const dataSubscription = config.data.subscribe(p => {
                    get().setItems(p.items, p.isFetching);
                });
                set({ dataSubscription, initialized: true });
                config.onFetched?.();
            }
            if (config.autoInitialFetch !== false && !get().fetching) {
                get().fetch({ forceFetch: false });
            }
        },
        uninit() {
            set({ initialized: false });
        },
        dispose() {
            set(p => ({ initializedCount: p.initializedCount - 1 }));
            if (get().dataSubscription && get().initializedCount === 0) {
                get().dataSubscription?.unsubscribe();
                set({ dataSubscription: null });
            }
        },
        setFilterData(name, data) {
            const filters = { ...get().dataFilters };
            if (data == null) {
                delete filters[name];
            } else {
                filters[name] = data;
            }
            set({ dataFilters: filters });
        },
        resetFilterData() {
            set({ dataFilters: {} });
        },
        setSortModel(sortModel) {
            set({ sortModel });
            get().fetch();
        },
        setSelectedItem(selectedItem: T | null) {
            set(p => ({ selectedItem, selectedItemId: selectedItem == null ? undefined : getId(selectedItem, p) }));
            get().selectedItem$.next(selectedItem);
        },
        setPath(path) {
            set({ path });
        },
        setQs(qs) {
            set({ qs });
        },
        setItems(items, isFetching) {
            items = config.addIndexId ? addIdIndex(items) : items;
            const filteredItems = applyFilters(items, get());
            const filteredSortedItems = applySort(filteredItems, get().sortModel);
            set({ items, filteredSortedItems, count: filteredSortedItems.length, fetching: isFetching });
            itemsSubject.next({ items: get().items, isFetching: get().fetching });
            refreshSelectedItem(get());
            autoSelectFirstItem(get());
        },
        setListProps(listProps) {
            set(p => ({ listProps, listPropsId: p.listPropsId + 1 }));
        },
        setExternalDataFilters(filters) {
            set({ externalDataFilters: filters });
            if (!get().initialized) {
                set({ externalFetchDataFilters: filters });
            }
        },
    }));
    return Object.assign(function <U = Omit<ListStore<T>, "init" | "dispose">>(selector: (store: ListStore<T>) => U = p => p as U, compare: (a: U, b: U) => boolean = shallow) {
        initStore(useStore);
        return useStore(selector, compare);
    }, { getState: useStore.getState });
}

export interface UseListStore<T> {
    (): ListStore<T>;
    <U = Omit<ListStore<T>, "init" | "dispose">>(selector?: (store: ListStore<T>) => U, compare?: (a: U, b: U) => boolean): U;
    getState(): ListStore<T>;
}

function initStore(useStore: UseListStore<any>) {
    useEffect(() => {
        useStore.getState().init();
        return () => useStore.getState().dispose();
    }, [useStore]);
}

export function getId<T>(p: T, store: ListStore<T>) {
    return store.config.getId ? store.config.getId!(p) : (p as { id: string | number; }).id;
}

function refreshSelectedItem<T>(store: ListStore<T>) {
    if (store.fetching) {
        return;
    }
    let selectedItemId = store.selectedItemId;
    let selectedItem = selectedItemId == null ? null : store.items.find(p => getId(p, store) === selectedItemId) || null;
    if (selectedItem == null && selectedItemId != null) {
        selectedItem = null;
    }
    store.setSelectedItem(selectedItem);
}

function autoSelectFirstItem<T>(store: ListStore<T>) {
    if (store.config.autoSelectFirstItem && store.selectedItem == null) {
        store.setSelectedItem(store.filteredSortedItems[0]);
    }
}

export async function listStoreFetch<T>(store: ListStore<T>, filter: FilterDto, page: PageDto, count: boolean, signal: AbortSignal | undefined, qs?: { [name: string]: string | number | null | undefined; }) {
    const query: FilterSortPageDto = {
        filter,
        sort: getSort(store),
        page,
        count,
    };
    const { data } = await createAxios().post<ListDto<T>>(store.path!, query, { signal, params: qs });
    const items = store.config.addIndexId ? addIdIndex(data.items) : data.items;
    return { data, items, filter };
}
